/**
 * Access-token retrieval and shared auth interceptors for Authorization: Bearer auth.
 *
 * auth-frontend sets the session JWT into a client-readable access cookie so it is readable here.
 * The cookie name varies by environment: production issues a per-session, dynamically-named cookie
 * (VfGAccess_<uuid> global operator token, VfAccess_<uuid> site-scoped), while lower environments
 * (e.g. dev2) issue the fixed name (VfGAccess / VfAccess). Reading by name-prefix covers both forms.
 *
 * CloudFront's fixed-name cookie whitelist drops the dynamically-named production cookies at the
 * edge, so any request that relies on cookie forwarding arrives unauthenticated and auth-service
 * withholds PII (email) — the cause of the 2026-07-16 loss of moderator email visibility. Forwarding
 * the token as an Authorization header is immune to that (headers are forwarded first-class), so
 * data-plane clients read the token here and send it as a header IN ADDITION to cookies. Cookies are
 * intentionally kept on: the PHP-core clients (rest_api_v2/v4) authenticate the console login
 * bootstrap via the PHP session cookie, and disabling cookies broke that (self-logout loop on dev2).
 *
 * The auth-service token-lifecycle client (auth.js) is the deliberate exception: its
 * /user_token/cookie_refresh endpoint authenticates with the refresh cookie, so it does not install
 * these interceptors.
 */

// Name prefixes, not exact names: match both the fixed-name (VfGAccess) and dynamically-named
// (VfGAccess_<uuid>) forms. The global operator token is preferred over the site-scoped one.
const GLOBAL_TOKEN_PREFIX = 'VfGAccess';
const SITE_TOKEN_PREFIX = 'VfAccess';

function readCookieByPrefix(cookieString, prefix) {
    const match = (cookieString || '')
        .split(';')
        .map((pair) => pair.trim())
        .find((pair) => pair.startsWith(`${ prefix }=`) || pair.startsWith(`${ prefix }_`));

    if (!match || !match.includes('=')) {
        return undefined;
    }

    return match.slice(match.indexOf('=') + 1).trim() || undefined;
}

export function readAccessToken(cookieString = typeof document === 'undefined' ? '' : document.cookie) {
    return readCookieByPrefix(cookieString, GLOBAL_TOKEN_PREFIX)
        || readCookieByPrefix(cookieString, SITE_TOKEN_PREFIX)
        || undefined;
}

function hasAuthorizationHeader(headers = {}) {
    return Object.keys(headers).some((key) => key.toLowerCase() === 'authorization');
}

/**
 * Request interceptor: ADDITIVELY forward the access token as an Authorization: Bearer header so
 * auth-service authenticates even when the CDN edge drops the dynamically-named token cookie in
 * production. Cookie auth is deliberately LEFT ENABLED (withCredentials is not touched): the same
 * interceptor is installed on the PHP-core clients (rest_api_v2/v4), whose login bootstrap relies
 * on the PHP session cookie — turning cookies off there logged the console out on load
 * (/users/current -> id:0 -> logout loop, CM-1682 dev2 regression). The header is the fix for the
 * edge-cookie-drop; dropping cookies was not required and broke session auth, so we keep both.
 *
 * A caller-provided Authorization header is preserved (never overwritten). A missing token yields
 * an unchanged request (cookie auth still applies) that createRefreshRetryHandler recovers on a 401.
 *
 * @param {object} config - the outgoing request config
 * @returns {object} the config with a Bearer header added when a token is available; cookies untouched
 */
export function authorizationRequestInterceptor(config) {
    if (hasAuthorizationHeader(config.headers)) {
        return config;
    }

    const token = readAccessToken();

    if (!token) {
        return config;
    }

    return {
        ...config,
        headers: {
            ...config.headers,
            Authorization: `Bearer ${ token }`,
        },
    };
}

/**
 * Response error interceptor factory: on a 401, refresh the token once and replay the request.
 * The stale Authorization header is stripped from the replayed config so authorizationRequestInterceptor
 * re-reads the freshly-refreshed token rather than resending the expired one. Guarded by hasRetried so a
 * still-failing refresh surfaces the 401 instead of looping.
 *
 * @param {object} api - the http-client instance to replay the request on
 * @param {Function} refreshToken - refreshes the session token (auth.js refreshUserToken); returns a promise
 * @returns {Function} a response-error interceptor that refreshes and replays on a 401
 */
export function createRefreshRetryHandler(api, refreshToken) {
    return (error) => {
        if (error.config && !error.config.hasRetried && error.response && error.response.status === 401) {
            return refreshToken().then(() => {
                // Strip the stale Authorization header (any casing) so authorizationRequestInterceptor
                // re-reads the refreshed token instead of treating the old one as caller-provided.
                const headers = {};
                Object.keys(error.config.headers || {}).forEach((key) => {
                    if (key.toLowerCase() !== 'authorization') {
                        headers[key] = error.config.headers[key];
                    }
                });

                return api.request({
                    ...error.config,
                    headers,
                    hasRetried: true,
                });
            });
        }

        return Promise.reject(error);
    };
}
